import React, { useState, useRef } from 'react';
import styles from './index.module.scss';
import { useScreenSlideIn } from '@/hooks/useScreenSlideIn';
import newAixueLogo from '@/assets/home/new-aixue-logo.png';
import newScreen3GradientBackground from '@/assets/home/new-screen3-gradient-background.png';
import newScreen3CircleBig from '@/assets/home/new-screen3-circle-big.png';
import newScreen3CircleSmall from '@/assets/home/new-screen3-circle-small.png';
import newScreen3CardBg1 from '@/assets/home/new-screen3-card-bg1.png';
import newScreen3CardBg2 from '@/assets/home/new-screen3-card-bg2.png';
import newScreen3CardBg3 from '@/assets/home/new-screen3-card-bg3.png';
import newScreen3CardBg4 from '@/assets/home/new-screen3-card-bg4.png';
import newScreen3Icon1 from '@/assets/home/new-screen3-icon1.png';
import newScreen3Icon2 from '@/assets/home/new-screen3-icon2.png';
import newScreen3Icon3 from '@/assets/home/new-screen3-icon3.png';
import newScreen3Icon4 from '@/assets/home/new-screen3-icon4.png';

const CARD_CONFIG = [
  {
    id: 1,
    icon: newScreen3Icon1,
    bg: newScreen3CardBg1,
    borderClass: styles['card-border-1'],
    title: '更专注',
    subtitle: '3分钟一次开口，学习专注不走神',
    desc: '坚信短时高频互动是学习效果的核心保障',
  },
  {
    id: 2,
    icon: newScreen3Icon2,
    bg: newScreen3CardBg2,
    borderClass: styles['card-border-2'],
    title: '更包容',
    subtitle: '问题答错无压力，AI导师更耐心',
    desc: '创造零压力试错环境，保护学习自信心',
  },
  {
    id: 3,
    icon: newScreen3Icon3,
    bg: newScreen3CardBg3,
    borderClass: styles['card-border-3'],
    title: '更专属',
    subtitle: '学习内容个性化，AI授课一对一',
    desc: '拒绝标准化灌输，尊重每个学习者的独特性',
  },
  {
    id: 4,
    icon: newScreen3Icon4,
    bg: newScreen3CardBg4,
    borderClass: styles['card-border-4'],
    title: '更科学',
    subtitle: 'AI导师方法好，真人辅助效果好',
    desc: '融合AI算法与真人教育智慧，实现教学最优解',
  },
];

const CARD_TRANSITION_MS = 280;

export default function Screen3() {
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const [collapsingCardId, setCollapsingCardId] = useState<number | null>(null);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCardEnter = (id: number) => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
    const prev = activeCard;
    setActiveCard(id);
    if (prev !== id && prev != null) {
      setCollapsingCardId(prev);
      collapseTimerRef.current = setTimeout(() => {
        setCollapsingCardId(null);
        collapseTimerRef.current = null;
      }, CARD_TRANSITION_MS);
    }
  };

  const handleCardAreaLeave = () => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
    if (activeCard != null) {
      setCollapsingCardId(activeCard);
      setActiveCard(null);
      collapseTimerRef.current = setTimeout(() => {
        setCollapsingCardId(null);
        collapseTimerRef.current = null;
      }, CARD_TRANSITION_MS);
    }
  };

  const slideInRef = useScreenSlideIn({
    triggerSlideRatio: 0.8,
    animationClassName: styles['screen3-anim-start'],
  });

  return (
    <section className={styles['screen3-cover']} ref={slideInRef.ref}>
      <div className={styles['screen3-wrap']}>
        {/* 标题 */}
        <div className={styles['screen3-title-container']}>
          <h2 className={styles['screen3-title']}>产品四大特点</h2>
        </div>

        {/* 中心区域 + 四张卡片；鼠标离开时四卡均为小卡 */}
        <div className={styles['screen3-center-wrapper']} onMouseLeave={handleCardAreaLeave}>
          <div className={styles['screen3-center']}>
            <div className={styles['screen3-center-circles']}>
              <img src={newScreen3CircleBig} alt="" className={styles['screen3-circle-big']} loading="lazy" />
              <img src={newScreen3CircleSmall} alt="" className={styles['screen3-circle-small']} loading="lazy" />
            </div>
            {/* 渐变背景固定在圆心，不参与入场动画，避免位移 */}
            <div className={styles['screen3-center-gradient']}>
              <img
                src={newScreen3GradientBackground}
                alt=""
                className={styles['screen3-gradient-bg']}
                loading="lazy"
              />
            </div>
            <div className={styles['screen3-logo-container']}>
              <img src={newAixueLogo} alt="爱学" className={styles['screen3-logo']} loading="lazy" />
            </div>
          </div>

          {CARD_CONFIG.map(({ id, icon, bg, borderClass, title, subtitle, desc }) => (
            <div
              key={id}
              className={`${styles['screen3-card-wrap']} ${styles[`screen3-card-${id}`]} ${activeCard === id ? styles['screen3-card-expanded'] : ''} ${collapsingCardId === id ? styles['screen3-card-collapsing'] : ''}`}
              onMouseEnter={() => handleCardEnter(id)}
            >
              <div className={styles['screen3-card-bg']}>
                <img src={bg} alt="" loading="lazy" />
              </div>
              <div className={`${styles['screen3-card']} ${borderClass}`}>
                <div className={styles['screen3-card-inner']}>
                  <div className={styles['screen3-card-content']}>
                    <h3 className={styles['screen3-card-title']}>{title}</h3>
                    <p className={styles['screen3-card-subtitle']}>{subtitle}</p>
                    <p className={styles['screen3-card-desc']}>{desc}</p>
                  </div>
                  <div className={styles['screen3-card-icon']}>
                    <img src={icon} alt="" loading="lazy" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
